/**
 * API service for OPL (Open Issue List) management.
 */

import apiClient from './client';
import type {
  OplAttachment,
  OplComment,
  OplIssueCreate,
  OplIssueDetail,
  OplIssueListItem,
  OplIssueQueryParams,
  OplIssueUpdate,
  OplOverviewResponse,
  OplVersionItem,
  OperationLog,
  PaginatedResponse,
} from '@/types';

// Issues
export const listIssues = async (
  projectId: number,
  params?: OplIssueQueryParams,
): Promise<PaginatedResponse<OplIssueListItem>> => {
  const response = await apiClient.get<PaginatedResponse<OplIssueListItem>>(
    `/opl/${projectId}/issues/`,
    { params },
  );
  return response.data;
};

export const getIssue = async (
  projectId: number,
  issueId: number,
): Promise<OplIssueDetail> => {
  const response = await apiClient.get<OplIssueDetail>(
    `/opl/${projectId}/issues/${issueId}/`,
  );
  return response.data;
};

export const createIssue = async (
  projectId: number,
  data: OplIssueCreate,
): Promise<OplIssueDetail> => {
  const response = await apiClient.post<OplIssueDetail>(
    `/opl/${projectId}/issues/`,
    data,
  );
  return response.data;
};

export const updateIssue = async (
  projectId: number,
  issueId: number,
  data: OplIssueUpdate,
): Promise<OplIssueDetail> => {
  const response = await apiClient.put<OplIssueDetail>(
    `/opl/${projectId}/issues/${issueId}/`,
    data,
  );
  return response.data;
};

export const deleteIssue = async (
  projectId: number,
  issueId: number,
): Promise<void> => {
  await apiClient.delete(`/opl/${projectId}/issues/${issueId}/`);
};

// Overview
export const getOverview = async (
  projectId: number,
): Promise<OplOverviewResponse> => {
  const response = await apiClient.get<OplOverviewResponse>(
    `/opl/${projectId}/overview/`,
  );
  return response.data;
};

// Comments
export const listComments = async (
  projectId: number,
  issueId: number,
): Promise<OplComment[]> => {
  const response = await apiClient.get<OplComment[]>(
    `/opl/${projectId}/issues/${issueId}/comments/`,
  );
  return response.data;
};

export const createComment = async (
  projectId: number,
  issueId: number,
  content: string,
): Promise<OplComment> => {
  const response = await apiClient.post<OplComment>(
    `/opl/${projectId}/issues/${issueId}/comments/`,
    { content },
  );
  return response.data;
};

export const deleteComment = async (
  projectId: number,
  issueId: number,
  commentId: number,
): Promise<void> => {
  await apiClient.delete(
    `/opl/${projectId}/issues/${issueId}/comments/${commentId}/`,
  );
};

// Attachments
export const listAttachments = async (
  projectId: number,
  issueId: number,
): Promise<OplAttachment[]> => {
  const response = await apiClient.get<OplAttachment[]>(
    `/opl/${projectId}/issues/${issueId}/attachments/`,
  );
  return response.data;
};

export const createAttachment = async (
  projectId: number,
  issueId: number,
  data: { file_name: string; file_url: string; object_key: string; file_size: number },
): Promise<OplAttachment> => {
  const response = await apiClient.post<OplAttachment>(
    `/opl/${projectId}/issues/${issueId}/attachments/`,
    data,
  );
  return response.data;
};

export const deleteAttachment = async (
  projectId: number,
  issueId: number,
  attachmentId: number,
): Promise<void> => {
  await apiClient.delete(
    `/opl/${projectId}/issues/${issueId}/attachments/${attachmentId}/`,
  );
};

// Operation logs
export const getIssueLogs = async (
  projectId: number,
  issueId: number,
): Promise<{ logs: OperationLog[] }> => {
  const response = await apiClient.get<{ logs: OperationLog[] }>(
    `/opl/${projectId}/issues/${issueId}/logs/`,
  );
  return response.data;
};

// Package-linked issues
export const getPackageIssues = async (
  packageId: number,
): Promise<OplIssueListItem[]> => {
  const response = await apiClient.get<OplIssueListItem[]>(
    `/packages/${packageId}/issues/`,
  );
  return response.data;
};

// Versions
export const getVersions = async (
  projectId: number,
): Promise<OplVersionItem[]> => {
  const response = await apiClient.get<OplVersionItem[]>(
    `/opl/${projectId}/versions/`,
  );
  return response.data;
};
